'use strict';

/**
 * The helpers exported in this file are used to calculate
 * details that will appear on the pdp. Copy for the details
 * titles is not determined in these helpers. These methods
 * return only the information that will appear under a detail's title.
 */

const findNameSpace = require('find-namespace-value');
const sharedHelpers = require('./detailsSharedLogic');
const constants = require('../../constants/detailsConstants.es');
const currencyFormat = require('dibs-currency-helpers/currencyFormat');
const { currencies: currencyConstants } = require('dibs-currency-helpers/constants');

module.exports = {
    /**
     * See definition for getSingleValueWithoutLink in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getDateOfManufacture(config) {
        return sharedHelpers.getSingleValueWithoutLink(config, 'classification.creationDate');
    },

    /**
     *
     * Get an array containing one object that DetailsItem.jsx will
     * use to render this detail. The object will have a key 'value',
     * which holds the display data for the date of manufacture.
     *
     * @deprecated
     * @param {object} config
     * @param {object} config.item              - Bunsen Item Model
     * @param {object} config.periodReferences  - see this.periodReferences in ItemStore for definition {@link newPdp/ItemStore}
     *
     * @returns {{
     *               value: string,
     *               isLink: boolean
     *          }[]}
     *
     * ie: [{
     *          value: '1990s',
     *          isLink: false
     *     }]
     */
    getDateOfManufactureFashion(config) {
        const { item, periodReferences } = config;
        const contents = [];

        // First check if the creationDate is available
        let date = item.get('classification.creationDate');
        let reference;

        // If the creationDate is not available,
        // grab the period and pass that through the jewelry
        // period reference set
        if (!date && periodReferences) {
            date = item.get('classification.period');
            reference = periodReferences[date];
        }

        // If there is a no creation date, but there is a period, return reference.displayName
        // If there is a creation date or no creation date and no period, return date
        const dateFormatted = reference && reference.displayName ? reference.displayName : date;

        // dateFormatted is now either a string or undefined
        if (dateFormatted) {
            contents.push({
                isLink: false,
                value: dateFormatted,
            });
        }

        return contents;
    },

    /**
     *
     * Get an array containing one object that DetailsItem.jsx will use
     * to render this detail. The object will have a key 'value', which
     * holds the display data for the dealer's location.
     *
     * @param {object} config
     * @param {object} config.item - Bunsen Item Model
     *
     * @returns {{
     *               value: string,
     *               isLink: boolean
     *          }[]}
     *
     * ie: [{
     *          value: 'New York, NY',
     *          isLink: false
     *     }]
     */
    getDealerLocation(config) {
        const contents = [];
        const item = config.item;

        const location = [];
        // We use the seller's shipping address to determine dealer location
        const city = item.get('seller.shippingAddress.city');
        const state = item.get('seller.shippingAddress.state');
        const country = item.get('seller.shippingAddress.country');
        let shouldPushValue = false;

        if (city) {
            location.push(city);
        }

        // Calculate the location only if the seller has a ciy and country
        shouldPushValue = location.length === 1 && country;

        if (shouldPushValue && country === 'US' && state) {
            // If the seller is located in the US,
            // dealer location should be formatted: City, State
            location.push(state);
        } else if (shouldPushValue) {
            // If the seller is located outside the US,
            // dealer location should be formatted: City, Country
            location.push(country);
        }

        // Should be true only if seller has city and state or
        // city and country already pushed into location array
        if (location.length === 2) {
            const locationFormatted = location.join(', ');
            contents.push({
                isLink: false,
                value: locationFormatted,
            });
        }

        return contents;
    },

    /**
     * See definition for getSingleValueWithoutLink in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getDealerReference(config) {
        return sharedHelpers.getSingleValueWithoutLink(config, 'dealerReference');
    },

    /**
     * @param config
     * @param {boolean} config.item - bunsen item model
     * @param {object} config.copy  - pdp copy
     * @returns {Array}
     *
     * ie: [
     *         {
     *             isLink: true,
     *             link: '/nydc/',
     *             value: '1stdibs Gallery'
     *         },
     *         {
     *             isLink: false,
     *             value: '200 Lexington'
     *         },
     *         {
     *             isLink: false,
     *             value: 'New York, NY'
     *         }
     *
     *     ]
     */
    getItemLocation(config) {
        const { item, copy } = config;
        const isNydc = item.isNydc() && item.get('seller.associations.nydcStatus') === 'Y';
        const contents = [];

        if (isNydc) {
            const { nydc: { galleryName, urlPath, streetAddress, cityStateAddress } } = copy;

            contents.push({
                isLink: true,
                link: urlPath,
                value: galleryName,
            });

            contents.push({
                isLink: false,
                value: streetAddress,
            });

            contents.push({
                isLink: false,
                value: cityStateAddress,
            });
        }

        return contents;
    },

    /**
     * See definition for getSingleValueWithoutLink in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getSetSize(config) {
        const { item } = config;
        const setSize = item.get('attributes.set_size');

        if (!setSize) {
            return null;
        }

        const model = setSize.first();

        const setSizeItem = sharedHelpers.getSingleValueFromModelWithoutLink({
            model,
            modelKey: 'text',
        });
        const setSizeValue = setSizeItem[0].value;

        if (!setSizeValue || parseInt(setSizeValue) === 1) {
            return null;
        }
        setSizeItem[0].value = `Set of ${setSizeValue}`;

        return setSizeItem;
    },

    /**
     * See definition for getSingleValueWithoutLink in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getReferenceNumber(config) {
        return sharedHelpers.getSingleValueWithoutLink(config, 'dibsReference');
    },

    /**
     *
     * Get an array of objects that DetailsVersions.jsx will
     * use to render this detail. Each object will have a key 'value',
     * which holds the display data for an item's version.
     *
     * @param {object} config
     * @param {object} config.item - Bunsen Item Model
     *
     * @returns {{
     *               value: string
     *          }[]}
     *
     * ie: [{
     *          value: '75x53 Price: $7,800'
     *     }]
     */
    getVersions(config) {
        const { item, copy } = config;
        const contents = [];
        const versions = item.get('versions');
        const currency = config.currency || currencyConstants.CURRENCY_USD;
        const priceCopy = findNameSpace('details.price', copy);

        if (!versions) {
            return contents;
        }

        versions.each(version => {
            const description = version.get('description');
            const financialAmount = version.get('price');
            let price;
            let data;

            // Need to check that financialAmount.get('amount') is defined,
            // because getDisplayPrice will return $0 if it is undefined.
            if (financialAmount && financialAmount.get('amount')) {
                price = currencyFormat.format(currency, null, financialAmount, {
                    showDecimal: false,
                });
            }

            if (description && price) {
                data = `${description} ${priceCopy} ${price}`;
            } else if (description) {
                data = description;
            }

            if (data) {
                contents.push({
                    value: data,
                });
            }
        });

        return contents;
    },

    getLeadTime(config) {
        const { item } = config;
        const leadTime = item.get('attributes.lead_time');

        if (!leadTime) {
            return null;
        }

        const model = leadTime.first();

        const leadTimeItem = sharedHelpers.getSingleValueFromModelWithoutLink({
            model,
            modelKey: 'option.name',
        });
        const leadTimeValue = leadTimeItem[0].value;
        if (leadTimeItem && leadTimeValue !== constants.availableNow) {
            leadTimeItem[0].value = `It will take ${leadTimeValue} to make this piece`;
        }

        return leadTimeItem;
    },

    getProductionType(config) {
        const { item } = config;
        const productionType = item.get('attributes.production_type');

        if (!productionType) {
            return null;
        }

        const model = productionType.first();

        return sharedHelpers.getSingleValueFromModelWithoutLink({ model, modelKey: 'option.name' });
    },
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/details/helpers/detailsMiscellaneous.es.js